import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { CheckCircle } from 'lucide-react';
import { useState, useEffect } from 'react';

export default function PatientsPage() {
  const [showTypeform, setShowTypeform] = useState(false);
  const [showCoreWellnessTypeform, setShowCoreWellnessTypeform] = useState(false);

  useEffect(() => {
    if (showTypeform || showCoreWellnessTypeform) {
      const script = document.createElement('script');
      script.src = "//embed.typeform.com/next/embed.js";
      script.async = true;
      document.body.appendChild(script);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
        const tfWidgets = document.querySelectorAll('div[data-tf-widget], iframe[data-tf-widget]');
        tfWidgets.forEach(widget => widget.remove());
        const tfPopups = document.querySelectorAll('div[data-tf-popup]');
        tfPopups.forEach(popup => popup.remove());
      };
    }
  }, [showTypeform, showCoreWellnessTypeform]);

  return (
    <div className="min-h-screen flex flex-col bg-[#f3f0e9] font-sans text-[#2e3a33]">
      <Navbar />
      <main className="flex-grow">
        {/* Page Header Section */}
        <section className="py-32 bg-[#f3f0e9]">
          <div className="container mx-auto px-6 max-w-4xl text-center">
            <h1 className="text-5xl md:text-7xl font-serif font-normal tracking-tight mb-12 text-[#2e3a33]">
              Redefine Your Healthspan
            </h1>
            <p className="text-xl md:text-2xl text-[#737973] leading-relaxed max-w-3xl mx-auto font-light">
              Two flagship programs crafted by a Harvard-trained physician & AI architect to help you live longer, stronger, and sharper.
            </p>
          </div>
        </section>

        {/* Offerings Section - Now with a very light gray background for subtle separation */}
        <section className="py-24 bg-[#f3f0e9]">
          <div className="container mx-auto px-6 max-w-6xl">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-5xl font-serif font-normal text-[#2e3a33] mb-10">
                Choose Your Path to Longevity
              </h2>
              <p className="text-lg text-[#737973] max-w-3xl mx-auto leading-relaxed font-light">
                Whether you seek comprehensive, physician-led medical care or expert wellness guidance, we offer a pathway designed to elevate your health journey.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-stretch">
              {/* Offering 1: Concierge Longevity Medicine Practice */}
              <div className="p-10 flex flex-col h-full space-y-8">
                <h3 className="text-2xl md:text-3xl font-serif font-normal text-[#2e3a33] mb-3">
                  Concierge Longevity Medicine Practice
                </h3>
                <p className="text-base text-[#737973] font-medium mb-8">Elite Physician-Led Program for Proactive Longevity Medicine</p>
                <p className="text-gray-700 mb-6 leading-relaxed text-sm">
                  A white-glove medical relationship for those who want end-to-end oversight, proactive disease prevention, and performance optimization—powered by precision biomarkers and advanced AI.
                </p>
                <h4 className="text-lg font-semibold text-indigo-900 mb-3">Key Features & Benefits:</h4>
                <ul className="space-y-3 text-gray-700 mb-8 flex-grow text-sm">
                  {[
                    "<strong class='text-[#2e3a33]'>In-Depth Baseline Assessment:</strong> Beyond standard check-ups. We utilize advanced diagnostics, full-genome sequencing (if applicable), comprehensive biomarker analysis, and functional tests to create a complete 360° view of your health.",
                    "<strong class='text-[#2e3a33]'>Continuous Physician Partnership:</strong> Up to four extensive medical consultations annually, complemented by unlimited secure messaging for ongoing support, adjustments, and peace of mind.",
                    "<strong class='text-[#2e3a33]'>Precision Treatment Protocols:</strong> AI-augmented, data-driven plans including targeted nutritional strategies, hormone optimization, advanced supplement regimens, and prescription management where clinically appropriate.",
                    "<strong class='text-[#2e3a33]'>Proactive Health Navigation:</strong> Seamless coordination with specialists, diagnostic labs, and careful integration with your existing healthcare providers to ensure holistic and efficient care.",
                    "<strong class='text-[#2e3a33]'>Priority Access & Support:</strong> Direct access to Dr. Vinjamoori for urgent concerns, ensuring you receive expert guidance promptly when you need it most."
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-1" />
                      <span dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <p className="text-[#737973] mb-8 text-sm leading-relaxed"><strong className="text-[#2e3a33]">Ideal for:</strong> Individuals committed to a transformative health journey, those with complex health profiles, executives seeking peak performance, and anyone desiring the highest caliber of proactive, personalized medical care.</p>
                  <button 
                    onClick={() => setShowTypeform(true)} 
                    className="w-full bg-[#2e3a33] text-white hover:bg-[#4a5a50] font-medium py-3 px-8 rounded-full text-sm shadow-md"
                  >
                    Request Concierge Membership
                  </button>
                  <p className="mt-6 pt-3 text-xs text-[#5f6d63] p-3 text-center">
                    Establishes a formal doctor-patient relationship. <strong>Limited to 10 patients.</strong>
                  </p>
                </div>
              </div>

              {/* Offering 2: Longevity Core Wellness Program */}
              <div className="p-10 flex flex-col h-full space-y-8">
                <h3 className="text-2xl md:text-3xl font-serif font-normal text-[#2e3a33] mb-3">
                  Longevity Core Wellness Program
                </h3>
                <div className="flex items-center mb-4">
                  <span className="text-xl font-normal text-[#2e3a33] mr-1">$99</span>
                  <span className="text-[#737973]">per month</span>
                </div>
                <p className="text-base text-[#737973] font-medium mb-8">A personalized coaching program focused on foundational health and longevity strategies.</p>
                <p className="text-gray-700 mb-6 leading-relaxed text-sm">
                  Perfect for self-starters who crave expert analysis of their labs and a personalized, science-backed roadmap to sustained vitality—no prescriptions or MD relationship required.
                </p>
                <h4 className="text-lg font-semibold text-indigo-900 mb-3">Key Features & Benefits:</h4>
                <ul className="space-y-3 text-gray-700 mb-8 flex-grow text-sm">
                  {[
                    "<strong class='text-[#2e3a33]'>Quarterly AI-Powered Wearable Data Analysis:</strong> Comprehensive review of your HRV, CGM, sleep, and other wearable data.",
                    "<strong class='text-[#2e3a33]'>Personalized Action Plans & Supplement Discounts:</strong> Receive a detailed nutrition, exercise, and sleep plan, plus discounts on preferred supplements.",
                    "<strong class='text-[#2e3a33]'>Exclusive Monthly Q&A Access:</strong> Participate in group Q&A sessions with Dr. Vinjamoori to gain clarity and learn from a community of proactive individuals.",
                    "<strong class='text-[#2e3a33]'>Curated Longevity Content:</strong> Receive regular, evidence-based insights, research summaries, and deep dives into cutting-edge longevity topics with practical implementation guidance."
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-1" />
                      <span dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <p className="text-[#737973] mb-8 text-sm leading-relaxed"><strong className="text-[#2e3a33]">Ideal for:</strong> Individuals proactive about their wellness, those with a primary physician seeking specialized longevity insights, or anyone wanting to optimize health with expert, non-medical guidance.</p>
                  <button 
                    onClick={() => setShowCoreWellnessTypeform(true)} 
                    className="w-full bg-[#2e3a33] text-white hover:bg-[#4a5a50] font-medium py-3 px-8 rounded-full text-sm shadow-md"
                  >
                    Start Your Longevity Core Wellness Program
                  </button>
                  <p className="mt-6 pt-3 text-xs text-[#5f6d63] p-3 text-center">
                    This is an educational and coaching program, not medical care. Does not establish a doctor-patient relationship.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Technology Expertise Section (Moved to Bottom) */}
      <section className="py-24 md:py-32 bg-[#f3f0e9]">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 className="text-4xl md:text-6xl font-serif font-bold text-[#2e3a33] mb-12 text-center">
            A Different Kind of Doctor
          </h2>
          <p className="text-[#737973] leading-relaxed text-lg mb-6 font-light">
            In an era where medicine often feels like a series of disconnected data points and generic advice, Dr. Vinjamoori offers something refreshingly different. His background isn't just in treating patients; it's in architecting the AI systems that are revolutionizing healthcare. As a Harvard-trained physician and former Director of Product at Virta Health, he didn’t just use advanced tools—he built them, leading teams that deployed sophisticated AI to transform patient outcomes at scale.
          </p>
          <p className="text-[#737973] leading-relaxed text-lg mb-8 font-light">
            This unique dual expertise means your care is powered by an unparalleled understanding of how to leverage machine intelligence. We use AI not as a gimmick, but as a powerful analytical engine to:
          </p>
          <ul className="space-y-4 text-[#737973] mb-8 text-lg pl-6 list-disc marker:text-[#2e3a33] font-light">
            <li><strong className="text-[#2e3a33] font-medium">Decode Complexity:</strong> Unravel intricate patterns in your biomarker data that traditional methods might miss, identifying root causes, not just symptoms.</li>
            <li><strong className="text-[#2e3a33] font-medium">Predict & Prevent:</strong> Proactively identify potential health risks years before they manifest, shifting from reactive treatment to proactive optimization.</li>
            <li><strong className="text-[#2e3a33] font-medium">Personalize Precisely:</strong> Craft hyper-personalized health protocols tailored to your unique biology, lifestyle, and goals with a level of detail previously unimaginable.</li>
            <li><strong className="text-[#2e3a33] font-medium">Accelerate Insights:</strong> Deliver comprehensive health assessments and actionable strategies in a fraction of the time it takes conventional clinics.</li>
          </ul>
          <p className="text-[#737973] leading-relaxed text-lg font-medium text-center mt-10">
            This is more than just modern medicine. It's the <strong className="text-[#2e3a33]">next generation of healthcare</strong>, designed by a physician who builds the future.
          </p>
        </div>
      </section>
      <Footer />
      
      {/* Typeform Modal for Concierge Program */}
      {showTypeform && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl relative">
            <button 
              onClick={() => setShowTypeform(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 z-10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div className="p-1 h-[80vh]">
              <div data-tf-live="01JV2C93JHCP7RNHMKT5H00WE4" data-tf-height="100%"></div>
            </div>
          </div>
        </div>
      )}

      {/* Typeform Modal for Core Wellness Program */}
      {showCoreWellnessTypeform && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl relative">
            <button 
              onClick={() => setShowCoreWellnessTypeform(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 z-10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div className="p-1 h-[80vh]">
              <div data-tf-live="01JV556J1S5SFRE1BQSH0H2GY2" data-tf-height="100%"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
